import * as React from 'react';
import '../App.css';

import TextField from "./TextField";

// Possible parameters of the TextFieldRegEx
export interface ITextFieldRegExProps {
    /** mandatory property to define a regular expression for checking */
    regex: string;
    /** style */
    style?: React.CSSProperties;
    /** password field will only show asterics instead of what was typed in */
    password?: boolean;
    /** The content to use for the floating label element. */
    floatingLabelText?: React.ReactNode;
    /** current text */
    value?: string | number;
    /** text shown for the empty TextFieldRegEx */
    placeholder?: string;
    /** property to enable/disable focus */
    disabled?: boolean;
    /** property to enable/disable input */
    readonly?: boolean;
    /** defines if to show underline */
    underlineShow?: boolean;
    /** optional property to select the groups from the regular expression to pass as results onBlur */
    groupindexes?: number[];
    /** property to define the input is invalid */
    errorText?: React.ReactNode;
    /** id to make unique identification of component */
    id?: string;

    /**
     * will be called on focus of component
     * @param newValue current value
     * @param matchGroups matching groups, or null if the newValue doesn't match the regular expression
     * @param regExError defined if the regular expression couldn't be compiled
     */
    onFocus?(newValue: string, matchGroups: string[] | null, regExError?: string): void;
    /**
     * will be called on blur of component
     * @param event event
     * @param newValue current value
     * @param matchGroups matching groups, or null if the newValue doesn't match the regular expression
     * @param regExError defined if the regular expression couldn't be compiled
     */
    onBlur?(newValue: string, matchGroups: string[] | null, regExError?: string): void;
    /**
     * will be called on change of the value of component
     * @param event event
     * @param newValue current value
     * @param matchGroups matching groups, or null if the newValue doesn't match the regular expression
     * @param regExError defined if the regular expression couldn't be compiled
     */
    onChange?(newValue: string, matchGroups: string[] | null, regExError?: string): void;
}

interface ITextFieldRegExState {
    value: string; // current text
    regexp: RegExp | string; // compiled regexp
    matchGroups: string[] | null; // selected groups from the regular expression
}

class TextFieldRegEx extends React.Component<ITextFieldRegExProps, ITextFieldRegExState>
{
    node: TextField | null;

    constructor(props: ITextFieldRegExProps) {
        super(props);

        // check the set value is correct
        const re = this.checkRegEx(this.props.regex);
        const v = props.value ? props.value.toString() : "";
        this.state = this.getNewState(re, v);

        // This binding is necessary to make `this` work in the callback
        this.onChange = this.onChange.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onFocus = this.onFocus.bind(this);
        this.callOnChangeIfError = this.callOnChangeIfError.bind(this);
    }

    componentDidMount() {
        this.callOnChangeIfError();
    }

    callOnChangeIfError() {
        const {regexp, matchGroups, value} = this.state;
        const isRegExpError = !(regexp instanceof RegExp);
        if (isRegExpError || matchGroups === null) {
            // error state
            if (this.props.onChange) {
                this.props.onChange(value, matchGroups, isRegExpError ? regexp as string : undefined);
            }
        }
    }

    componentWillReceiveProps(newProps: ITextFieldRegExProps) {
        // check the regex is changed
        let up = false;
        let re: RegExp | string = this.state.regexp;
        if (newProps.regex !== this.props.regex) {
            up = true;
            re = this.checkRegEx(newProps.regex);
        }

        // check the value is changed
        let v = this.state.value;
        if (newProps.value !== this.props.value) {
            up = true;
            v = newProps.value ? newProps.value.toString() : "";
        }

        if (up) {
            const newState = this.getNewState(re, v);
            this.setState(newState, () => this.callOnChangeIfError());
        }
    }

    onChange(event: React.FormEvent<{}>, newValue: string) {
        const newState = this.getNewState(this.state.regexp, newValue);
        this.setState(newState, () => {
            if (this.props.onChange) {
                const {regexp} = this.state;
                const isRegExpError = !(regexp instanceof RegExp);
                this.props.onChange(newValue, newState.matchGroups, isRegExpError ? regexp as string : undefined);
            }
        });
    }

    onBlur(event: React.FocusEvent<{}>) {
        if (this.props.onBlur) {
            const {regexp, matchGroups, value} = this.state;
            const isRegExpError = !(regexp instanceof RegExp);
            this.props.onBlur(value, matchGroups, isRegExpError ? regexp as string : undefined);
        }
    }

    onFocus(event: React.FocusEvent<{}>) {
        if (this.props.onFocus) {
            const {regexp, matchGroups, value} = this.state;
            const isRegExpError = !(regexp instanceof RegExp);
            this.props.onFocus(value, matchGroups, isRegExpError ? regexp as string : undefined);
        }
    }

    blur(): void {
        if (this.node)
            this.node.blur();
    }

    focus(): void {
        if (this.node)
            this.node.focus();
    }

    select(): void {
        if (this.node)
            this.node.select();
    }

    getValue(): string {
        return this.node ? this.node.getValue() : '';
    }

    getInputNode(): HTMLInputElement | null {
        return this.node ? this.node.getInputNode() : null;
    }

    render() {
        // defined the properties of TextField before use
        let {value, onChange, onBlur, onFocus,
            ...tfProps} = this.props;

        Object.assign(tfProps, {
            value: this.state.value,
            onChange: this.onChange,
            onBlur: onBlur ? this.onBlur : undefined,
            onFocus: onFocus ? this.onFocus : undefined
        });

        return (
            <TextField
                {...tfProps}
                ref={(el) => this.node = el}
            />
        );
    }

    // checks the regexp and returns either RegExp or an error string
    private checkRegEx(regex: string): RegExp | string {
        let re: RegExp | string;
        try {
            re = new RegExp(regex);
        }
        catch (ex) {
            re = ex.toString();
        }
        return re;
    }

    // checks the newValue to the regexp and generates new state
    // if the regexp is not RegExp, it's an error string
    private getNewState(regexp: RegExp | string, newValue: string): ITextFieldRegExState {
        const matchGroups = regexp instanceof RegExp ? this.matchGroups(regexp, newValue) : null;
        let newState: ITextFieldRegExState =
        {
            value: newValue,
            regexp: regexp,
            matchGroups: matchGroups
        };
        return newState;
    }

    // checks, if the newValue matchs exactly the reg ex.
    // if yes, returns the matched groups (when defined) as string[]
    // if no, returns null
    private matchGroups(regexp: RegExp, newValue: string): string[] | null {
        let matchGroups = regexp.exec(newValue);
        let result: string[] | null = null;
        if (matchGroups && matchGroups[0] === matchGroups.input) {
            result = matchGroups.slice(1, matchGroups.length);
        }
        return result;
    }
}
export default TextFieldRegEx;



// WEBPACK FOOTER //
// ./src/components/TextFieldRegEx.tsx